import { PERMISSIONS } from "@/constants/permissions"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { useCanViewOrgRegulations } from "@/features/material-compliance/hooks/useCanViewOrgRegulations"
import useUser from "@/hooks/useUser"
import { useGetUserFreeTrialsLeftData } from "@/services/api/userService"
import type { SettingsSectionId } from "./paths"

/** `pending`: whether the page applies is still loading (the trial lookup);
 *  `error`: that lookup failed, so it isn't known yet. */
export type SectionVisibility = "visible" | "hidden" | "pending" | "error"

const show = (visible: boolean): SectionVisibility =>
  visible ? "visible" : "hidden"

/**
 * Whether the Material Compliance module is open: it is closed while the org
 * is in data-room onboarding (RequireDataRoomInactive), and so are the
 * settings that came from its pages.
 */
export function useMaterialComplianceOpen(): boolean {
  const flags = useGetAllFeatureFlags()
  return flags.MATERIAL_COMPLIANCE && !flags.ONBOARDING_DATA_ROOM
}

/**
 * Who sees the supplier inquiry settings (sending address, email signature,
 * inquiry emails and reminders): the people who could open their old dialog,
 * from the Supplier Studio inquiries.
 */
export function useInquirySettingsAccess(): boolean {
  const flags = useGetAllFeatureFlags()
  const { hasPermission } = useUser()
  const mcOpen = useMaterialComplianceOpen()
  return (
    mcOpen &&
    flags.MC_SUPPLIER_STUDIO &&
    flags.MC_SUPPLIER_STUDIO_INQUIRY &&
    hasPermission(PERMISSIONS.MC_INQUIRIES_VIEW)
  )
}

/**
 * Who sees the declaration defaults (supplier data freshness, validity, scope
 * limits): whoever issues declarations of conformity, or sends inquiries.
 */
export function useDeclarationDefaultsAccess(): boolean {
  const flags = useGetAllFeatureFlags()
  const { hasPermission } = useUser()
  const mcOpen = useMaterialComplianceOpen()
  const inquirySettings = useInquirySettingsAccess()
  return (
    inquirySettings ||
    (mcOpen &&
      flags.MC_CUSTOMER_STUDIO &&
      flags.MC_CUSTOMER_STUDIO_CONFORMITY &&
      hasPermission(PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW))
  )
}

/**
 * Which settings pages this user gets. The same flags and permissions that
 * gated each setting where it lived before, so nobody gains or loses access
 * by the move. Drives both the nav and the route guard.
 */
export function useSettingsVisibility(): Record<
  SettingsSectionId,
  SectionVisibility
> {
  const flags = useGetAllFeatureFlags()
  const { hasPermission } = useUser()
  const canViewOrgRegulations = useCanViewOrgRegulations()
  const mcOpen = useMaterialComplianceOpen()
  const inquirySettings = useInquirySettingsAccess()
  const declarationDefaults = useDeclarationDefaultsAccess()
  const {
    data: trial,
    isLoading: trialLoading,
    isError: trialError,
  } = useGetUserFreeTrialsLeftData()
  // Paid plans have no trial data; only users actually on a trial get the page.
  const onTrial = (trial?.freeTrial.totalFreeTrialDurationDays ?? 0) > 0
  const mc = flags.MATERIAL_COMPLIANCE
  // The sending address and signature are common to both studios: an org
  // that only issues declarations sends their emails from the same address,
  // with the same signature. Still only with the inquiry permission the
  // backend checks on its endpoints, so the page never opens onto a refusal.
  const customerEmailSending =
    mcOpen &&
    flags.MC_CUSTOMER_STUDIO &&
    flags.MC_CUSTOMER_STUDIO_CONFORMITY &&
    hasPermission(PERMISSIONS.MC_CUSTOMER_DECLARATIONS_VIEW) &&
    hasPermission(PERMISSIONS.MC_INQUIRIES_VIEW)

  return {
    setup: "visible",
    profile: "visible",
    // Either channel: the notification emails link here too.
    notifications: show(
      flags.NOTIFICATIONS_IN_APP || flags.NOTIFICATIONS_EMAIL,
    ),
    signatures: show(flags.ENABLE_ORG_SIGNATURES),
    // A failed lookup is no proof the user isn't on a trial.
    usage: trialLoading
      ? "pending"
      : trialError && !trial
        ? "error"
        : show(onTrial),
    general: "visible",
    emailSending: show(inquirySettings || customerEmailSending),
    members: show(flags.TEAM_MANAGEMENT),
    regulations: show(canViewOrgRegulations),
    // Also reached from the master-data pages, by people who may change it.
    masterData: show(
      mcOpen &&
        (inquirySettings ||
          (flags.MC_MASTER_DATA &&
            hasPermission(PERMISSIONS.MC_MASTER_DATA_VIEW) &&
            hasPermission(PERMISSIONS.MC_INQUIRIES_SEND))),
    ),
    // Templates, the place in the signature box, the declaration defaults.
    conformityDeclarations: show(
      (mc && (flags.MC_DECLARATION_TEMPLATES || flags.ENABLE_ORG_SIGNATURES)) ||
        declarationDefaults,
    ),
    supplierInquiries: show(inquirySettings),
  }
}
